'use client';

import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import logger from '@/utils/client-logger';
import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import type {
  StyleGuideCardItem,
  StyleGuideCards2026Props,
  StyleGuideCards2026TrackingParams,
} from './types';

const COMPONENT_ID = 'styleGuideCards2026';
const DEFAULT_HEADING_COLOR = '#1A1C1E';
const DEFAULT_SUBTITLE_COLOR = '#626668';
const DEFAULT_TEXT_COLOR = 'var(--color-primary-white)';
//  fr 展示4个，其他6个
const MAX_CARD_COUNT = 6;
const FOUR_CARD_COUNT = 4;

const formatText = (text?: string) => text?.replace(/<br\s*\/?>/gi, '\n').trim() || '';

const getImageSize = (value: string | number | undefined, fallback: number) => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : fallback;
};

const buildJumpTarget = (item: StyleGuideCardItem): JumpTarget => ({
  jump_type: item.jump_type,
  Jump_target: item.Jump_target ?? undefined,
  ...(item.sku?.shop_sku ? { sku: { shop_sku: item.sku.shop_sku } } : undefined),
  ...(item.product_group?.id || item.product_group?.path
    ? { product_group: item.product_group }
    : undefined),
  ...(item.crowd_recommend?.id ? { crowd_recommend: { id: item.crowd_recommend.id } } : undefined),
});

const buildTrackingParams = ({
  item,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
}: {
  item: StyleGuideCardItem;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
}): StyleGuideCards2026TrackingParams => ({
  module_index: moduleIndex,
  module_name: componentId,
  module_id: moduleId,
  module_title: moduleTitle,
  mkt_index: originalIndex,
  mkt_index_sub: '',
  mkt_jump_type: String(item.jump_type || ''),
  mkt_jump_content: String(item.Jump_target || ''),
  module_tab_name: '',
  coupon_id_list: '',
  coupon_name_list: '',
  shop_sku: String(item.sku?.shop_sku || ''),
  business_sku: String(item.sku?.business_sku || ''),
  product_sku: String(item.sku?.sku || ''),
  product_id: String(item.sku?.id || ''),
  product_name: String(item.sku?.short_name || ''),
  group_id: String(item.product_group?.id || ''),
  group_name: String(item.product_group?.name || ''),
});

function useTrackingHandlers({
  trackingParams,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<StyleGuideCards2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: StyleGuideCards2026TrackingParams;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC style guide card exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC style guide card click tracking error:', error);
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return {
    cardRef: useMergedRefs(exposureRef, validExposureRef),
    handleClick,
  };
}

function StyleGuideCard({
  item,
  cardCount,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: StyleGuideCardItem;
  cardCount: number;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
} & Pick<StyleGuideCards2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'>) {
  const trackingParams = useMemo(
    () =>
      buildTrackingParams({
        item,
        originalIndex,
        componentId,
        moduleId,
        moduleTitle,
        moduleIndex,
      }),
    [componentId, item, moduleId, moduleIndex, moduleTitle, originalIndex],
  );
  const jumpTarget = useMemo(() => buildJumpTarget(item), [item]);
  const { cardRef, handleClick } = useTrackingHandlers({
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });

  const title = formatText(item.title);
  const description = formatText(item.des);
  const titleColor = item.title_color?.trim() || DEFAULT_TEXT_COLOR;
  const descriptionColor = item.des_color?.trim() || titleColor;

  return (
    <div ref={cardRef} className="h-full min-w-0">
      <MagicJump
        prefetch
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={title}
        className="block h-full w-full"
      >
        <article
          className={`relative h-full w-full overflow-hidden bg-auxiliary-light-gray ${
            cardCount === FOUR_CARD_COUNT ? 'rounded-[12px]' : ''
          }`}
        >
          <Image
            src={item.jump_image || ''}
            alt=""
            width={getImageSize(item.jump_img_width, 317)}
            height={getImageSize(item.jump_img_height, 555)}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            priority={moduleIndex < 3}
            sizes={
              cardCount === FOUR_CARD_COUNT
                ? 'calc((100vw - 114px) / 4)'
                : `${Number((100 / cardCount).toFixed(3))}vw`
            }
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[8px] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.48)_100%)] p-[clamp(16px,1.25vw,24px)]">
            <h3
              className="overflow-hidden whitespace-nowrap font-bb text-[clamp(20px,1.4583vw,28px)] leading-[clamp(24px,1.7708vw,34px)]"
              style={{ color: titleColor }}
            >
              {title}
            </h3>
            {description ? (
              <p
                className="line-clamp-2 w-full whitespace-pre-line break-words font-br text-[clamp(12px,0.7292vw,14px)] leading-[clamp(15px,0.8854vw,17px)]"
                style={{ color: descriptionColor }}
              >
                {description}
              </p>
            ) : null}
          </div>
        </article>
      </MagicJump>
    </div>
  );
}

export default function StyleGuideCards2026({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: StyleGuideCards2026Props) {
  const validItems = useMemo(
    () =>
      (indexData.jump_target || [])
        .map((item, originalIndex) => ({ item, originalIndex }))
        .filter(({ item }) => Boolean(item.jump_image && formatText(item.title)))
        .slice(0, MAX_CARD_COUNT),
    [indexData.jump_target],
  );

  if (validItems.length === 0) return null;

  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || '');
  const moduleTitle = indexData.layout_title || '';
  const headingTitle = formatText(indexData.layout_title);
  const headingSubtitle = formatText(indexData.layout_subtitle);
  const shouldShowHeading =
    Number(indexData.layout_title_status) === 1 && Boolean(headingTitle || headingSubtitle);
  const headingTitleColor = indexData.layout_title_color?.trim() || DEFAULT_HEADING_COLOR;
  const headingSubtitleColor =
    indexData.layout_subtitle_color?.trim() ||
    indexData.layout_subtitl_color?.trim() ||
    DEFAULT_SUBTITLE_COLOR;

  return (
    <section data-component-id={componentId} className="w-full bg-primary-white">
      {shouldShowHeading ? (
        <header className="flex w-full flex-col items-center justify-center gap-[8px] px-[16px] py-[28px] text-center">
          {headingTitle ? (
            <h2
              className="w-full whitespace-pre-line font-bb text-[32px] leading-[39px]"
              style={{ color: headingTitleColor }}
            >
              {headingTitle}
            </h2>
          ) : null}
          {headingSubtitle ? (
            <p
              className="whitespace-pre-line font-br text-[20px] leading-[24px]"
              style={{ color: headingSubtitleColor }}
            >
              {headingSubtitle}
            </p>
          ) : null}
        </header>
      ) : null}

      <ul
        className={`flex w-full overflow-hidden ${
          validItems.length === FOUR_CARD_COUNT
            ? 'gap-[6px] px-[48px]'
            : 'h-[clamp(360px,28.90625vw,555px)] gap-[4px]'
        }`}
      >
        {validItems.map(({ item, originalIndex }) => (
          <li
            key={item.id || `${formatText(item.title)}-${originalIndex}`}
            className={`min-w-0 flex-1 ${
              validItems.length === FOUR_CARD_COUNT ? 'aspect-[4/5]' : ''
            }`}
          >
            <StyleGuideCard
              item={item}
              cardCount={validItems.length}
              originalIndex={originalIndex}
              componentId={componentId}
              moduleId={moduleId}
              moduleTitle={moduleTitle}
              moduleIndex={moduleIndex}
              onTrack={onTrack}
              onExpTrack={onExpTrack}
              onValidExpTrack={onValidExpTrack}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
